import React from 'react'
import { motion } from 'framer-motion'
import { FileText, Calendar, Mail, Phone } from 'lucide-react'
import SEO from '@components/SEO'

const TermsPage = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6, ease: 'easeOut' }
  }

  const currentDate = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  })

  return (
    <div className="pt-14">
      <SEO 
        title="Terms of Service - Total Rewards Pro"
        description="Terms of Service and conditions for using Total Rewards Pro's compensation consulting services and website."
        keywords="terms of service, legal terms, conditions, total rewards pro, compensation consulting"
      />
      
      {/* Hero Section */}
      <section className="pt-14 lg:pt-16 pb-20 lg:pb-32 text-white relative overflow-hidden">
        {/* Background Image with WebP fallback */}
        <picture className="absolute inset-0">
          <source srcSet="/terms-hero.webp" type="image/webp" />
          <img 
            src="/terms-hero.jpg" 
            alt="Professional corporate office lobby"
            className="absolute inset-0 w-full h-full object-cover"
            loading="eager"
            decoding="async"
          />
        </picture>
        
        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-black/60 to-black/80"></div>
        
        {/* Subtle pattern overlay */}
        <div className="absolute inset-0 pattern-dots opacity-10"></div>
        
        <div className="max-w-4xl mx-auto w-full px-6 sm:px-8 lg:px-12 text-center relative">
          <motion.div {...fadeInUp}>
            <div className="w-16 h-16 bg-gradient-to-br from-primary-400 to-primary-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-professional">
              <FileText className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-display-2 text-white mb-6">
              Terms of Service
            </h1>
            <p className="text-body text-neutral-200 mb-4">
              Please read these terms and conditions carefully before using our services
            </p>
            <div className="flex items-center justify-center space-x-2 text-sm text-muted">
              <Calendar className="w-4 h-4" />
              <span>Last updated: {currentDate}</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Terms Content */}
      <section className="section-padding">
        <div className="max-w-4xl mx-auto w-full px-6 sm:px-8 lg:px-12">
          <motion.div 
            className="prose prose-lg max-w-none"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="space-y-8">
              {/* Introduction */}
              <div className="card p-8">
                <h2 className="text-heading-1 text-navy-900 mb-4">1. Introduction</h2>
                <p className="text-body text-muted mb-4">
                  Welcome to Total Rewards Pro ("we," "our," or "us"). These Terms of Service ("Terms") govern your use of our website located at https://total-rewards-pro.web.app (the "Service") operated by iTotal Rewards Pro LLP.
                </p>
                <p className="text-body text-muted">
                  By accessing or using our Service, you agree to be bound by these Terms. If you disagree with any part of these terms, then you may not access the Service.
                </p>
              </div>

              {/* Services */}
              <div className="card p-8">
                <h2 className="text-heading-1 text-navy-900 mb-4">2. Our Services</h2>
                <p className="text-body text-muted mb-4">
                  Total Rewards Pro provides strategic compensation and benefits consulting services, including but not limited to:
                </p>
                <ul className="list-disc list-inside text-body text-muted space-y-2 mb-4">
                  <li>Equity compensation design and implementation</li>
                  <li>Executive compensation planning</li>
                  <li>Regional compensation benchmarking</li>
                  <li>Market analysis and benchmarking</li>
                  <li>General rewards strategy</li>
                  <li>Benefits advisory services</li>
                </ul>
                <p className="text-body text-muted">
                  All services are provided subject to a separate service agreement that will govern the specific terms of our engagement.
                </p>
              </div>

              {/* Use of Website */}
              <div className="card p-8">
                <h2 className="text-heading-1 text-navy-900 mb-4">3. Use of Website</h2>
                <p className="text-body text-muted mb-4">
                  You may use our website for lawful purposes only. You agree not to use the website:
                </p>
                <ul className="list-disc list-inside text-body text-muted space-y-2 mb-4">
                  <li>In any way that violates any applicable federal, state, local, or international law or regulation</li>
                  <li>To transmit, or procure the sending of, any advertising or promotional material, or any other form of similar solicitation</li>
                  <li>To impersonate or attempt to impersonate the Company, a Company employee, another user, or any other person or entity</li>
                  <li>To engage in any other conduct that restricts or inhibits anyone's use or enjoyment of the website</li>
                </ul>
              </div>

              {/* Intellectual Property */}
              <div className="card p-8">
                <h2 className="text-heading-1 text-navy-900 mb-4">4. Intellectual Property Rights</h2>
                <p className="text-body text-muted mb-4">
                  The Service and its original content, features, and functionality are and will remain the exclusive property of iTotal Rewards Pro LLP and its licensors. The Service is protected by copyright, trademark, and other laws. Our trademarks and trade dress may not be used in connection with any product or service without our prior written consent.
                </p>
              </div>

              {/* User Content */}
              <div className="card p-8">
                <h2 className="text-heading-1 text-navy-900 mb-4">5. User Content</h2>
                <p className="text-body text-muted mb-4">
                  Our Service may allow you to post, link, store, share and otherwise make available certain information, text, graphics, videos, or other material ("Content"). You are responsible for the Content that you post to the Service, including its legality, reliability, and appropriateness.
                </p>
                <p className="text-body text-muted">
                  By posting Content to the Service, you grant us the right and license to use, modify, publicly perform, publicly display, reproduce, and distribute such Content on and through the Service.
                </p>
              </div>

              {/* Privacy */}
              <div className="card p-8">
                <h2 className="text-heading-1 text-navy-900 mb-4">6. Privacy Policy</h2>
                <p className="text-body text-muted">
                  Your privacy is important to us. Please review our Privacy Policy, which also governs your use of the Service, to understand our practices.
                </p>
              </div>

              {/* Prohibited Uses */}
              <div className="card p-8">
                <h2 className="text-heading-1 text-navy-900 mb-4">7. Prohibited Uses</h2>
                <p className="text-body text-muted mb-4">
                  You may not use our Service:
                </p>
                <ul className="list-disc list-inside text-body text-muted space-y-2">
                  <li>For any unlawful purpose or to solicit others to perform unlawful acts</li>
                  <li>To violate any international, federal, provincial, or state regulations, rules, laws, or local ordinances</li>
                  <li>To infringe upon or violate our intellectual property rights or the intellectual property rights of others</li>
                  <li>To harass, abuse, insult, harm, defame, slander, disparage, intimidate, or discriminate</li>
                  <li>To submit false or misleading information</li>
                  <li>To upload or transmit viruses or any other type of malicious code</li>
                </ul>
              </div>

              {/* Disclaimers */}
              <div className="card p-8">
                <h2 className="text-heading-1 text-navy-900 mb-4">8. Disclaimers</h2>
                <p className="text-body text-muted mb-4">
                  The information on this website is provided on an "as is" basis. To the fullest extent permitted by law, this Company:
                </p>
                <ul className="list-disc list-inside text-body text-muted space-y-2">
                  <li>Excludes all representations and warranties relating to this website and its contents</li>
                  <li>Excludes all liability for damages arising out of or in connection with your use of this website</li>
                </ul>
              </div>

              {/* Limitation of Liability */}
              <div className="card p-8">
                <h2 className="text-heading-1 text-navy-900 mb-4">9. Limitation of Liability</h2>
                <p className="text-body text-muted">
                  In no case shall iTotal Rewards Pro LLP, nor its directors, employees, partners, agents, suppliers, or affiliates, be liable for any indirect, incidental, punitive, consequential, or special damages. Our liability shall not exceed the amount paid by you, if any, for accessing our Service.
                </p>
              </div>

              {/* Termination */}
              <div className="card p-8">
                <h2 className="text-heading-1 text-navy-900 mb-4">10. Termination</h2>
                <p className="text-body text-muted">
                  We may terminate or suspend your access immediately, without prior notice or liability, for any reason whatsoever, including without limitation if you breach the Terms.
                </p>
              </div>

              {/* Governing Law */}
              <div className="card p-8">
                <h2 className="text-heading-1 text-navy-900 mb-4">11. Governing Law</h2>
                <p className="text-body text-muted">
                  These Terms shall be interpreted and governed by the laws of India, without regard to its conflict of law provisions. Our failure to enforce any right or provision of these Terms will not be considered a waiver of those rights.
                </p>
              </div>

              {/* Changes to Terms */}
              <div className="card p-8">
                <h2 className="text-heading-1 text-navy-900 mb-4">12. Changes to Terms</h2>
                <p className="text-body text-muted">
                  We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a revision is material, we will try to provide at least 30 days' notice prior to any new terms taking effect.
                </p>
              </div>

              {/* Contact Information */}
              <div className="card p-8 bg-primary-50 border-primary-200">
                <h2 className="text-heading-1 text-navy-900 mb-4">13. Contact Us</h2>
                <p className="text-body text-muted mb-6">
                  If you have any questions about these Terms of Service, please contact us:
                </p>
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <Mail className="w-5 h-5 text-primary-600" />
                    <a href="mailto:info@totalrewardspro.com" className="text-primary-600 hover:text-primary-700 font-medium">
                      info@totalrewardspro.com
                    </a>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Phone className="w-5 h-5 text-primary-600" />
                    <a href="tel:+919873655887" className="text-primary-600 hover:text-primary-700 font-medium">
                      +91-9873655887
                    </a>
                  </div>
                  <div className="text-body text-muted">
                    <strong>Address:</strong><br />
                    629, Khari Baoli Main Road<br />
                    Chandni Chowk<br />
                    Delhi – 110006<br />
                    India
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default TermsPage 